import React, { useEffect, useMemo, useState } from 'react'
import CreateFeedback from './createFeedback'
import { fetchFeedbacks } from '../../redux/feedback/action'
import { useAppDispatch, useAppSelector } from '../../redux/store'
import { Feedback } from '../../redux/feedback/types'
import { Box, Grid, Typography } from '@mui/material'
import { fontSizes, fontWeights } from '../Common/Typography/TypographyStyles'
import TableTemplate from '../Common/Table/TableTemplate'
import { selectLoadingFetchFeedbacks, selectErrorFetchFeedbacks } from '../../redux/feedback/selector'
import ViewImage from './viewImage'
import { backgroundColors } from '../Common/Color/Color'
import { selectUserRole } from '../../redux/auth/selector'
import { setDateFormat, setTimeFormat } from '../../utils/dateUtils'

interface Column {
  field: string
  headerName: string
  headerAlign?: 'left' | 'right' | 'center'
  bodyAlignment?: 'center' | 'right' | 'left'
  width?: string | number
  renderCell?: (value: any, item: any) => React.ReactNode
  renderFormat?: (value: any) => React.ReactNode
  sortable: boolean
}

const FeedbackPage: React.FC = () => {
  const userRole = useAppSelector(selectUserRole)
  const dispatch = useAppDispatch()
  const [feedbackData, setFeedbackData] = useState<Feedback[]>([])
  const loading = useAppSelector(selectLoadingFetchFeedbacks)
  const error = useAppSelector(selectErrorFetchFeedbacks)
  const state = { loading, error }

  const [expandedRowId, setExpandedRowId] = useState<string | null>(null)

  const handleRowClick = (item: Feedback) => {
    setExpandedRowId((prev) => (prev === item._id! ? null : item._id!))
  }

  const fetchData = React.useCallback(async () => {
    try {
      const data = await dispatch(fetchFeedbacks())

      if (fetchFeedbacks.fulfilled.match(data)) {
        setFeedbackData(data.payload)
      } else {
        console.warn('Fetch failed:', data.payload)
      }
    } catch (error) {
      console.error('Error fetching feedbacks:', error)
    }
  }, [dispatch])

  useEffect(() => {
    if (userRole === 'admin') fetchData()
  }, [fetchData])

  const columns = useMemo<Column[]>(
    () => [
      {
        field: 'feedback',
        headerName: 'FEEDBACK',
        bodyAlignment: 'center',
        sortable: true,
        renderCell: (param: string, item: Feedback) => (
          <Typography
            sx={{
              width: 600,
              whiteSpace: expandedRowId === item._id ? 'normal' : 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              textAlign: 'left',
              display: 'block',
              margin: '0 auto',
              fontSize: fontSizes.size12,
              fontWeight: fontWeights[700],
            }}
          >
            {param}
          </Typography>
        ),
      },
      {
        field: 'email',
        headerName: 'CREATED BY',
        bodyAlignment: 'center',
        sortable: true,
      },
      {
        field: 'createdAt',
        headerName: 'DATE CREATED',
        bodyAlignment: 'center',
        sortable: true,
        renderFormat: (value: string) => (
          <Grid container spacing={0} direction='column' alignContent='center'>
            <Grid>
              <Typography
                sx={{
                  fontSize: fontSizes.size12,
                  fontWeight: fontWeights[700],
                  lineHeight: 'ratio9to8',
                  letterSpacing: 'loose',
                }}
              >
                {setDateFormat(value)}
              </Typography>
            </Grid>
            <Grid>
              <Typography
                sx={{
                  fontSize: fontSizes.size12,
                  lineHeight: 'ratio9to8',
                  letterSpacing: 'loose',
                }}
              >
                {setTimeFormat(value)}
              </Typography>
            </Grid>
          </Grid>
        ),
      },
      {
        field: 'imageFileName',
        headerName: 'IMAGE',
        bodyAlignment: 'center',
        sortable: false,
        renderCell: (imageFileName: string) => <ViewImage imageFileName={imageFileName} />,
      },
    ],
    [expandedRowId],
  )

  return (
    <React.Fragment>
      {userRole === 'admin' ? (
        <Box
          component='div'
          sx={{ p: 0, backgroundColor: backgroundColors.white, width: 'auto' }}
          borderRadius={2}
          boxShadow={1}
        >
          <Grid
            container
            sx={{ p: 2 }}
            flexDirection='row'
            alignItems={'center'}
            justifyContent='space-between'
          >
            <Grid>
              <CreateFeedback userRole={userRole} onCreate={fetchData} />
            </Grid>
          </Grid>
          <TableTemplate
            columns={columns}
            datas={feedbackData}
            state={state}
            uniqueField='id'
            onRowClick={handleRowClick}
          />
        </Box>
      ) : (
        <CreateFeedback userRole={userRole} />
      )}
    </React.Fragment>
  )
}

export default FeedbackPage
